import { useAuth } from "@/contexts/AuthContext";
import { useBranch } from "@/contexts/BranchContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { User, LogOut, Settings, Moon, Sun, MapPin, Clock, DollarSign } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface RiderStats {
  activeOrders: number;
  todayDeliveries: number;
  todayEarnings: number;
  isOnline: boolean;
}

export const RiderHeader = () => {
  const { user, signOut } = useAuth();
  const { currentBranch } = useBranch();
  const { setTheme, actualTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [riderStats, setRiderStats] = useState<RiderStats>({
    activeOrders: 0,
    todayDeliveries: 0,
    todayEarnings: 0,
    isOnline: false
  });

  // Get rider information
  const riderInfo = useCallback(async () => {
    if (!user) return;
    
    try {
      // Get rider details
      const { data: rider, error: riderError } = await supabase
        .from('riders')
        .select('*, is_active')
        .eq('user_id', user.id)
        .single();

      if (riderError) {
        console.error('Error fetching rider:', riderError);
        return;
      }

      // Get today's stats
      const today = new Date().toISOString().split('T')[0];
      
      // Active orders
      const { data: activeOrders, error: activeError } = await supabase
        .from('orders')
        .select('id')
        .eq('assigned_to', rider.id)
        .in('status', ['assigned', 'picked_up', 'in_transit']);

      // Today's completed deliveries
      const { data: completedOrders, error: completedError } = await supabase
        .from('orders')
        .select('id, total_amount')
        .eq('assigned_to', rider.id)
        .eq('status', 'delivered')
        .gte('created_at', `${today}T00:00:00`)
        .lte('created_at', `${today}T23:59:59`);

      if (!activeError && !completedError) {
        const todayEarnings = completedOrders?.reduce((sum, order) => sum + (order.total_amount * 0.1), 0) || 0; // Assuming 10% commission

        setRiderStats({
          activeOrders: activeOrders?.length || 0,
          todayDeliveries: completedOrders?.length || 0,
          todayEarnings,
          isOnline: rider.is_active || false
        });
      }
    } catch (error) {
      console.error('Error loading rider stats:', error);
    }
  }, [user]);

  useEffect(() => {
    riderInfo();
    // Refresh stats every 30 seconds
    const interval = setInterval(riderInfo, 30000);
    return () => clearInterval(interval);
  }, [riderInfo]);

  const handleSignOut = async () => {
    try {
      await signOut();
      toast.success("Signed out successfully");
    } catch (error) {
      console.error('Sign out error:', error);
      toast.error("Error signing out");
    }
  };

  const toggleOnlineStatus = async () => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from('riders')
        .update({ is_active: !riderStats.isOnline })
        .eq('user_id', user.id);

      if (error) throw error;

      setRiderStats(prev => ({ ...prev, isOnline: !prev.isOnline }));
      toast.success(`You are now ${!riderStats.isOnline ? 'online' : 'offline'}`);
    } catch (error) {
      console.error('Error toggling status:', error);
      toast.error('Failed to update status');
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background border-b shadow-sm">
      <div className="flex items-center justify-between h-16 px-4">
        {/* Left section - Logo and status */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <img 
              src="/images/dmf_logo.png" 
              alt="Dough Man Foods" 
              className="h-8 w-8" 
            />
            <span className="font-bold text-lg text-primary">Rider Dashboard</span>
          </div>
          
          {/* Online Status Toggle */}
          <Button
            variant={riderStats.isOnline ? "default" : "outline"}
            size="sm"
            onClick={toggleOnlineStatus}
            className="gap-2"
          >
            <div className={`w-2 h-2 rounded-full ${riderStats.isOnline ? 'bg-green-500' : 'bg-gray-400'}`} />
            {riderStats.isOnline ? 'Online' : 'Offline'}
          </Button>
        </div>

        {/* Center section - Quick stats */}
        <div className="hidden md:flex items-center gap-6">
          <div className="flex items-center gap-2 text-sm">
            <MapPin className="h-4 w-4 text-blue-500" />
            <span className="font-medium">{riderStats.activeOrders}</span>
            <span className="text-muted-foreground">Active Orders</span>
          </div>
          
          <div className="flex items-center gap-2 text-sm">
            <Clock className="h-4 w-4 text-green-500" />
            <span className="font-medium">{riderStats.todayDeliveries}</span>
            <span className="text-muted-foreground">Today's Deliveries</span>
          </div>
          
          <div className="flex items-center gap-2 text-sm">
            <DollarSign className="h-4 w-4 text-yellow-500" />
            <span className="font-medium">₵{riderStats.todayEarnings.toFixed(2)}</span>
            <span className="text-muted-foreground">Today's Earnings</span>
          </div>
        </div>

        {/* Right section - Actions and profile */}
        <div className="flex items-center gap-3">
          {/* Branch indicator */}
          {currentBranch && (
            <Badge variant="outline" className="hidden sm:flex">
              {currentBranch.name}
            </Badge>
          )}

          {/* Theme Toggle */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setTheme(actualTheme === 'dark' ? 'light' : 'dark')}
            className="hover:bg-accent"
          >
            {actualTheme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="relative h-10 w-10 rounded-full">
                <Avatar className="h-10 w-10">
                  <AvatarImage src={user?.user_metadata?.avatar_url} alt={user?.email} />
                  <AvatarFallback className="bg-primary text-primary-foreground">
                    {user?.email ? getInitials(user.email) : 'R'}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56" align="end" forceMount>
              <div className="flex flex-col space-y-1 p-2">
                <p className="text-sm font-medium leading-none">{user?.email}</p>
                <p className="text-xs leading-none text-muted-foreground">
                  Rider Account
                </p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/rider/profile')}>
                <User className="mr-2 h-4 w-4" />
                <span>Profile</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/rider/settings')}>
                <Settings className="mr-2 h-4 w-4" />
                <span>Settings</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleSignOut}>
                <LogOut className="mr-2 h-4 w-4" />
                <span>Sign out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};

export default RiderHeader;